import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { SeverityBadge } from "./SeverityBadge";
import { parseUtc } from '../../../lib/datetime';

/**
 * ActivityCard — single row in the Pulse / Security feed.
 *
 * Anatomy (Phase 4 directive #5):
 *   ┌────────────────────────────────────────────────────────┐
 *   │ [severity]  Title                          time ago   │
 *   │ Plain-language description                             │
 *   │ ━ Actor · Module · Affected count                      │
 *   │ Optional propagation summary chip                      │
 *   │                                            View → CTA  │
 *   └────────────────────────────────────────────────────────┘
 *
 * Translation:
 *   `title` and `description` from the backend are i18n keys. We pass
 *   them through `t()`. If a key isn't in the bundle yet, t() falls back
 *   to the key string — acceptable as we ship Phase 4 with full coverage.
 */
export const ActivityCard = ({ event, onDeepLink }) => {
    const { t, i18n } = useTranslation();
    const nav = useNavigate();
    if (!event) return null;

    const lang = (i18n.language || "en").split("-")[0];
    const ago = relativeTime(event.created_at, lang);

    const handleNav = () => {
        if (onDeepLink) onDeepLink(event);
        else if (event.deep_link) nav(event.deep_link);
    };

    // v3.8.1 — Human Governance Language.
    // Resolve the module display name (registry-translated) before
    // interpolating the description template, so feed copy never leaks
    // raw module_keys (e.g. "review_shield") to operators.
    const moduleDisplay = resolveModuleDisplay(event, lang);

    const description = t(event.description, {
        module: moduleDisplay,
        organization: event.metadata?.organization_name || "",
        affected: event.affected_org_count || 0,
        reason: event.metadata?.reason || "",
        defaultValue: defaultDescription(event, moduleDisplay),
    });

    const titleText = t(event.title, {
        module: moduleDisplay,
        defaultValue: defaultTitle(event, moduleDisplay),
    });

    return (
        <article
            data-testid={`ob-activity-card-${event.id}`}
            data-severity={event.severity}
            className="ob-card p-5 sm:p-6 space-y-3"
        >
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap">
                    <SeverityBadge severity={event.severity} />
                    <h3
                        className="text-base"
                        style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}
                        data-testid="ob-activity-card-title"
                    >
                        {titleText}
                    </h3>
                </div>
                <time
                    className="text-xs whitespace-nowrap"
                    style={{ color: "var(--ob-text-tertiary)" }}
                    data-testid="ob-activity-card-time"
                >
                    {ago}
                </time>
            </header>

            <p
                className="text-sm leading-relaxed"
                style={{ color: "var(--ob-text-secondary)" }}
                data-testid="ob-activity-card-description"
            >
                {description}
            </p>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" style={{ color: "var(--ob-text-tertiary)" }}>
                {event.actor_name && (
                    <span className="inline-flex items-center gap-1">
                        <span aria-hidden="true">·</span>
                        <span>{event.actor_name}</span>
                    </span>
                )}
                {event.module_key && (
                    <span className="inline-flex items-center gap-1">
                        <span aria-hidden="true">·</span>
                        <span>{moduleDisplay}</span>
                    </span>
                )}
                {event.affected_org_count > 0 && (
                    <span className="inline-flex items-center gap-1">
                        <span aria-hidden="true">·</span>
                        <span>{t("ob.activity.affected_count", { count: event.affected_org_count })}</span>
                    </span>
                )}
            </div>

            {event.propagation_summary?.plain_language && (
                <div
                    data-testid="ob-activity-card-propagation"
                    className="text-xs px-3 py-2 rounded-lg"
                    style={{
                        background: "rgba(16,185,129,0.08)",
                        color: "#A7F3D0",
                        border: "1px solid rgba(16,185,129,0.20)",
                    }}
                >
                    {event.propagation_summary.plain_language}
                </div>
            )}

            {event.deep_link && (
                <div className="flex justify-end pt-1">
                    <button
                        type="button"
                        onClick={handleNav}
                        className="inline-flex items-center gap-1 text-xs hover:underline underline-offset-4"
                        style={{ color: "var(--ob-text-secondary)" }}
                        data-testid="ob-activity-card-deeplink"
                    >
                        {t("ob.activity.view_details")} <ArrowRight size={12} strokeWidth={1.5} />
                    </button>
                </div>
            )}
        </article>
    );
};

// Defaults shown only when an i18n key is missing — should be rare since
// Phase 4 ships full EN/FR/ES coverage, but we never want raw keys on screen.
function defaultTitle(event, moduleDisplay) {
    const m = moduleDisplay || "Module";
    const map = {
        module_locked: `${m} locked`,
        module_unlocked: `${m} unlocked`,
        module_scheduled: `${m} change scheduled`,
        schedule_expired: `${m} schedule expired`,
        break_glass_started: `${m} emergency override active`,
        break_glass_expired: `${m} emergency override ended`,
        local_override_created: `${m} setting changed`,
        local_override_removed: `${m} setting changed`,
        propagation_completed: "Propagation completed",
        security_denial: "Access denied",
    };
    return map[event.event_type] || "Activity";
}

function defaultDescription(event, moduleDisplay) {
    const m = moduleDisplay || event.module_key || "This setting";
    const org = event.metadata?.organization_name;
    return org ? `${m} affecting ${org}.` : "";
}

/**
 * Resolve the user-facing module name in the current locale.
 * Prefers `metadata.module_display.{lang}` (set server-side from registry),
 * then falls back to a title-cased version of the snake_case key.
 */
function resolveModuleDisplay(event, lang) {
    const md = event.metadata?.module_display;
    if (md && typeof md === "object") {
        return md[lang] || md.en || md.fr || md.es || "";
    }
    const key = event.module_key || event.metadata?.module || "";
    if (!key) return "";
    return key
        .split("_")
        .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : ""))
        .join(" ");
}

function relativeTime(iso, lang) {
    if (!iso) return "";
    const d = parseUtc(iso);
    const delta = Math.floor((Date.now() - d.getTime()) / 1000);
    if (delta < 60) return lang === "fr" ? "à l'instant" : lang === "es" ? "ahora" : "Just now";
    if (delta < 3600) return `${Math.floor(delta / 60)}m`;
    if (delta < 86400) return `${Math.floor(delta / 3600)}h`;
    if (delta < 86400 * 30) return `${Math.floor(delta / 86400)}d`;
    return d.toLocaleDateString(lang);
}
